/** @jsxImportSource @emotion/react */

import { ColDef, GetRowIdFunc } from "ag-grid-community";
import { ICellRendererParams } from "ag-grid-enterprise";
import { AgGridReact, CustomCellEditorProps } from "ag-grid-react";
import { useCallback, useMemo } from "react";
import { ResultsEditorRowDatum } from "components/ToolEditor/ToolEditor.types";
import { MultiBadgeRenderer } from "components/ToolEditor/components/MultiBadgeRenderer";

export type ToolEditorResultRowCellRendererParams =
  ICellRendererParams<ResultsEditorRowDatum>;

export type ToolEditorResultRowCellEditorParams =
  CustomCellEditorProps<ResultsEditorRowDatum>;

export type ToolSpecResultsColDef = ColDef<ResultsEditorRowDatum> & {
  colId: keyof ResultsEditorRowDatum | "controls";
};

interface Props {
  rowData: ResultsEditorRowDatum[];
}

export const ResultsViewer = ({ rowData }: Props) => {
  const colDefs: ToolSpecResultsColDef[] = useMemo(() => {
    return [
      {
        colId: "name" as const,
        field: "name",
        headerName: "Name",
        width: 150,
      },
      {
        colId: "file_patterns" as const,
        field: "file_patterns",
        headerName: "File Patterns",
        cellRenderer: (params: ToolEditorResultRowCellRendererParams) => (
          <MultiBadgeRenderer items={params.data?.file_patterns ?? []} />
        ),
        width: 150,
      },
      {
        colId: "description" as const,
        field: "description",
        headerName: "Description",
        flex: 1,
      },
    ];
  }, []);

  const getRowId: GetRowIdFunc<ResultsEditorRowDatum> = useCallback(
    (e) => e.data.key,
    [],
  );

  return (
    <AgGridReact<ResultsEditorRowDatum>
      className="ag-theme-balham-cell-borders"
      css={{ minWidth: 600 }}
      domLayout="autoHeight"
      columnDefs={colDefs}
      rowData={rowData}
      reactiveCustomComponents
      readOnlyEdit
      getRowId={getRowId}
    />
  );
};
